import type { Auth0ProviderWithConfigOptions } from '@auth0/auth0-react'
import type { QueryClientConfig } from '@tanstack/react-query'

/* ENV */
// Dev
export const DEBUG = import.meta.env.VITE_DEBUG === 'true'
export const ENVIRONMENT = import.meta.env.VITE_ENV as string
// Hosted App mode, set at build time by hosted-app.yml (or build:hosted locally): the app is served
// by CARTO under /app/<slug>/, which injects a per-viewer carto-info.json (token + apiBaseUrl). In
// this mode the app skips the Auth0 flow and runs as the logged-in CARTO user; their JWT feeds the
// USER token path (not the static one), so the suscriptor/ejecutivo role keeps resolving per viewer
// (see entry.tsx bootstrap and auth/hosted-app.ts).
export const HOSTED_APP = import.meta.env.VITE_HOSTED_APP === 'true'

// AI assistant
export const AI_ASSISTANT_ENABLED =
  import.meta.env.VITE_AI_ASSISTANT_ENABLED === 'true'
// WebSocket endpoint of the agent server (the assistant is unconfigured when empty)
export const AI_ASSISTANT_WS_URL =
  (import.meta.env.VITE_AI_ASSISTANT_WS_URL as string | undefined) ?? ''

// Satellite view: 'google' (default — the client settled on our own key, Slack 2026-08-19) or
// 'here' (the old carto2 parity per §78, keyless today). Without the API key of the chosen
// provider, the option is not listed (see `satellite.ts`).
export const SATELLITE_PROVIDER = import.meta.env
  .VITE_SATELLITE_PROVIDER as string
export const HERE_API_KEY = import.meta.env.VITE_HERE_API_KEY as string

// Google Maps config
export const GOOGLE_MAPS_API = import.meta.env.VITE_GOOGLE_API_KEY as string
export const GOOGLE_MAPS_ID = import.meta.env.VITE_GOOGLE_MAP_ID as string

// CARTO
export const CARTO_API_BASE_URL =
  (import.meta.env.VITE_CARTO_API_BASE_URL as string) ||
  `https://gcp-us-east1.api.carto.com`
export const CARTO_CLIENT_ID = import.meta.env.VITE_CARTO_CLIENT_ID as string
export const CARTO_ORGANIZATION_ID = import.meta.env
  .VITE_CARTO_ORGANIZATION_ID as string
export const CONNECTION_NAME = import.meta.env.VITE_CONNECTION_NAME as string
export let STATIC_ACCESS_TOKEN = import.meta.env.VITE_ACCESS_TOKEN as string
// Named sources: one prefix per environment (dev_/stg_/pro_), dev_ns_regions for instance
export const NS_PREFIX = import.meta.env.VITE_NS_PREFIX as string
// FQN of the exclusion table for the Table API (the Maps API demands a qualified
// project.dataset.table name; the bare name will not do). Per environment in the .env.
export const EXCLUSION_TABLE = import.meta.env.VITE_EXCLUSION_TABLE as string
// FQN of a STATIC (pre-generated) exclusion tileset. When set, the layer is served through the
// Tileset API instead of tiling the table at runtime (Table API): pre-simplified tiles, consistent
// across every zoom, without the dynamic path's aggressive clipping and simplification. Empty falls
// back to EXCLUSION_TABLE (today's behaviour, no regression). Per environment in the .env.
export const EXCLUSION_TILESET =
  (import.meta.env.VITE_EXCLUSION_TILESET as string | undefined) ?? ''
// FQN prefix (project.dataset) of the serving tables the catalogues read through the
// widgets model endpoint, which resolves neither named sources (decision log §30) nor
// bare table names. Per environment in the .env.
export const SERVING_DATASET = import.meta.env.VITE_SERVING_DATASET as string

// ADR-0005: consumption label client=<project>_<env>_<test|live>_<library>
export const ADR0005_PROJECT_SLUG =
  (import.meta.env.VITE_ADR0005_PROJECT_SLUG as string) ||
  'consorcio-seguros-chile'
export const TRAFFIC_MODE =
  (import.meta.env.VITE_TRAFFIC_MODE as string) || 'live'

// JWT claim carrying the CARTO platform roles. Same default the Cloud Functions read
// (functions/node/src/lib/config.ts `rolesClaim`): the two ends MUST look at the same claim or the
// front would gate one way and the API another.
export const CARTO_ROLES_CLAIM =
  (import.meta.env.VITE_CARTO_ROLES_CLAIM as string | undefined) ??
  'http://app.carto.com/roles'
// Manual-verification override for the business role ('suscriptor' | 'ejecutivo'). It exists
// because an `ejecutivo` session needs a viewer JWT nobody can mint locally. Honoured ONLY under
// the dev server (`import.meta.env.DEV`): a value leaking into a deployed .env would be a silent
// authorization bypass, so any real build compiles this to '' regardless of the env file.
export const FORCE_ROLE = import.meta.env.DEV
  ? (import.meta.env.VITE_FORCE_ROLE as string) || ''
  : ''
/* END ENV */

// Auth
export const AUTH: Auth0ProviderWithConfigOptions = {
  domain: 'auth.carto.com',
  clientId: CARTO_CLIENT_ID,
  authorizationParams: {
    scope: [
      'profile',
      'email',
      'read:current_user',
      'read:connections',
      'read:maps',
      'read:account',
    ].join(' '),
    audience: 'carto-cloud-native-api',
    // `organization` is only sent when there is a real org_. Consorcio's CARTO account has NO
    // Auth0 organisation (verified in userinfo: account ac_hupm77le carries no org_), so in dev it
    // stays empty: sending `organization=''` to /authorize would get the request rejected.
    ...(CARTO_ORGANIZATION_ID ? { organization: CARTO_ORGANIZATION_ID } : {}),
  },
}

// Where the CARTO Workspace's "Log out" ends: it signs the viewer out of CARTO (federated when the
// organization has SSO). Hosted mode's "Salir" ends there too (auth/hosted-app.ts).
export const CARTO_SIGN_OUT_URL = 'https://app.carto.com/logout'

// i18n
export const SUPPORTED_LANGUAGES = ['es']

export const DEFAULT_LOCALE = SUPPORTED_LANGUAGES[0]!

export const i18nConfig = {
  // This is the list of languages your application supports
  supportedLngs: SUPPORTED_LANGUAGES,
  // This is the language you want to use in case
  // if the user language is not in the supportedLngs
  fallbackLng: SUPPORTED_LANGUAGES[0],
  // The default namespace of i18next is "translation", but you can customize it here
  defaultNS: 'translation',
  debug: DEBUG,
}

// i18next fetches this at runtime, so Vite can't rewrite it like it does with static
// <script>/<link> refs. Prefix with BASE_URL (Vite's resolved `base`) so it follows wherever the
// bundle is served: '/' on the standard build, the absolute /app/<slug>/ path on the Hosted App
// build — no HOSTED_APP branching. The base must be absolute (not './'): with history routing a
// deep-link entry would otherwise resolve this against the current route's directory.
export const LOCALES_PATH = `${import.meta.env.BASE_URL}locales/{{lng}}/{{ns}}.json`

// React Query
export const REACT_QUERY_CONFIG: QueryClientConfig = {
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      // Coming back to the tab does NOT re-ask. React Query refetches every stale query on window
      // focus by default, and everything this app reads through it goes to the CARTO SQL API, whose
      // consumption is billed and tagged (ADR-0005). Nothing here changes server-side while the user
      // has the page open, so those requests buy nothing. Measured: one refocus after the stale time
      // cost one extra request per catalogue.
      refetchOnWindowFocus: false,
    },
  },
}

export const REACT_QUERY_DEVTOOL_INITIAL_STATE = false

export function setDynamicSettings({
  accessToken,
}: {
  accessToken: string | null
}) {
  STATIC_ACCESS_TOKEN = accessToken ?? STATIC_ACCESS_TOKEN
}
